import { RowItemType, Section, SelectorIdentifier, UIElementType } from '../../models';
import { RainInfoFieldIds, RainSectionIds } from './base-config-models';
import { field } from '../../core/field-schema';
import { required, min, max, pattern } from '../../core/validator-builders';
import { ComportOption, SampleRateElement } from '../../../models/location-card.model';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { RainAlertLocationCardState, RainAlertMonitorConfiguration } from './models';

/**
 * RainAlert Info Configuration Section
 */
export const rainInfo = (state: RainAlertLocationCardState): Section => {
    const config = state.monitorConfiguration as RainAlertMonitorConfiguration;

    return {
        info: {
            id: RainSectionIds.RainInfo,
            title: 'ADD_EDIT.RAINALERT',
            displayTitle: true,
            expandable: true,
            expanded: false,
            panelClass: 'rainalert-section',
        },
        rows: [
            {
                info: {
                    id: 'subtitle-row',
                },
                items: [
                    {
                        id: 'rainalert-subtitle',
                        type: RowItemType.UI,
                        uiType: UIElementType.Text,
                        label: 'ADD_EDIT.ADD_EDIT_SUBTITLE',
                        panelClass: 'input-text subtitle-text',
                    },
                ],
            },
            {
                info: {
                    id: 'sensor-row',
                    panelClass: 'each-Item',
                },
                items: [
                    field.select<ComportOption, string>(RainInfoFieldIds.Sensor, 'sensor', {
                        placeholder: 'ADD_EDIT.SENSOR',
                        panelClass: 'full-width',
                        value: 'ADD_EDIT.RAIN',
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.RainSensorOptions,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                    }),
                ],
            },

            // Intensity Interval row
            {
                info: {
                    id: 'intensity-interval-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    {
                        id: 'intensity-interval-label',
                        type: RowItemType.UI,
                        uiType: UIElementType.Text,
                        label: 'ADD_EDIT.RAIN_INTENSITY_INTERVAL',
                        panelClass: 'each-Item item-flex-center',
                    },
                    field.number(RainInfoFieldIds.IntensityInterval, 'intensityinterval', {
                        placeholder: 'ADD_EDIT.MINUTE',
                        panelClass: 'each-Item',
                        value: config?.intensityinterval ?? 60,
                        inputType: 'number',
                        integerOnly: true,
                        min: 1,
                        max: 1440,
                        step: 1,
                        validators: [
                            required({ message: 'HOME.MAP.MONITOR_SETTINGS.RAIN_INTENSITY_VALIDATION' }),
                            min({ value: 1, message: 'HOME.MAP.MONITOR_SETTINGS.RAIN_INTENSITY_VALIDATION' }),
                            max({ value: 1440, message: 'HOME.MAP.MONITOR_SETTINGS.RAIN_INTENSITY_VALIDATION' }),
                            pattern({
                                value: REGEX_CONFIG.numeric1_1440Pattern,
                                message: 'HOME.MAP.MONITOR_SETTINGS.RAIN_INTENSITY_VALIDATION',
                            }),
                        ],
                    }),
                ],
            },

            // Store Temperature and Battery row
            {
                info: {
                    id: 'store-temperature-battery-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.toggle(RainInfoFieldIds.StoreTemperature, 'storetemperature', {
                        label: 'ADD_EDIT.STORE_MONITOR_TEMPERATURE',
                        panelClass: 'each-Item item-flex-center',
                        value: config ? config.storetemperature : false,
                    }),
                    field.select<SampleRateElement, number>(RainInfoFieldIds.StoreBattery, 'storebattery', {
                        placeholder: 'HOME.MAP.MONITOR_SETTINGS.STORE_BATTERY_PLACEHOLDER',
                        panelClass: 'each-Item',
                        value: config?.storebattery ?? 1,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        optionsSelector: SelectorIdentifier.BatteryVoltageOptions,
                    }),
                ],
            },

            // Log Intensity row
            {
                info: {
                    id: 'log-intensity-row',
                    panelClass: 'custom-50p text-bottom-space',
                },
                items: [
                    field.toggle(RainInfoFieldIds.LogIntensity, 'logintensity', {
                        placeholder: 'ADD_EDIT.LOG_INTENSITY',
                        panelClass: 'each-Item item-flex-center toggle-reverse',
                        value: config?.logintensity ?? false,
                    }),
                ],
            },

            // Log UK Intensity row
            {
                info: {
                    id: 'log-uk-intensity-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.toggle(RainInfoFieldIds.LogUkIntensity, 'logukintensity', {
                        placeholder: 'ADD_EDIT.LOG_UK_INTENSITY',
                        panelClass: 'each-Item item-flex-center toggle-reverse',
                        value: config?.logukintensity ?? false,
                        disabled: !config || config.samplerate !== 120,
                    }),
                ],
            },
        ],
    };
};
